// src/routes/AppRoutes.jsx
import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import Login from '../components/Login';
import Arrivee from '../pages/Arrivee';
import Salles from '../pages/Salles';
import Passages from '../pages/Passages';
import FichePatient from '../pages/FichePatient';
import Sortie from '../pages/Sortie';
import SortiePatient from '../pages/SortiePatient';
import Todos from '../pages/Todos';
import AdminSortie from '../pages/AdminSortie';
import PageDebug from '../pages/PageDebug';
import CotationWizard from '../components/CotationWizard';
import AdminUsers from '../pages/AdminUsers';
import AdminUsers2 from '../pages/AdminUsers2';
import AdminAudit from '../pages/AdminAudit';
import AdminActes from '../pages/AdminActes';
import AdminTraumato from '../pages/AdminTraumato';
import AdminSallesGroupes from '../pages/AdminSallesGroupes';
import AdminPrintCodes from '../pages/AdminPrintCodes';
import ScanCodeScreen from "../pages/ScanCodeScreen";
import AdminParametres from '../pages/AdminParametres';
import AdminEquipements from '../pages/AdminEquipements';
import AdminTodoConfig from '../pages/AdminTodoConfig';
import AdminFormationContinue from '../pages/AdminFormationContinue';
import AdminStats from '../pages/AdminStats';
import ArretTravail from '../pages/ArretTravail';
import FormulaireArretTravail from '../pages/FormulaireArretTravail';
import Scanner from '../pages/Scanner';

// wrappers de protection
function PrivateRoute({ children }) {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  return token ? children : <Navigate to="/login" replace />;
}
function AdminRoute({ children }) {
  const role = localStorage.getItem('role') || sessionStorage.getItem('role');
  return role === 'admin' ? children : <Navigate to="/arrivee" replace />;
}

const RequireAdmin = ({ children }) => {
  const role = (sessionStorage.getItem('role') || localStorage.getItem('role') || '').toLowerCase();
  return role === 'admin' ? children : <div className="p-6">Accès refusé.</div>;
};

// petite anim slide
function PageSlide({ children }) {
  return (
    <motion.div
      initial={{ x: 40, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -40, opacity: 0 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

export default function AppRoutes({ patients, setPatients }) {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        {/* public */}
        <Route path="/login" element={<Login />} />

        {/* protégées */}
        <Route
          path="/arrivee"
          element={
            <PrivateRoute>
              <PageSlide>
                <Arrivee
                  patients={patients.filter((p) => !p.isFinalized)}
                  setPatients={setPatients}
                />
              </PageSlide>
            </PrivateRoute>
          }
        />

       <Route
          path="/scanner"
          element={
            <PrivateRoute>
              <PageSlide>
                <Scanner />
              </PageSlide>
            </PrivateRoute>
          }
        />

        <Route
          path="/salles"
          element={
            <PrivateRoute>
              <PageSlide>
                <Salles
                  patients={patients.filter((p) => !p.isFinalized)}
                  setPatients={setPatients}
                />
              </PageSlide>
            </PrivateRoute>
          }
        />

        <Route
          path="/passages"
          element={
            <PrivateRoute>
              <PageSlide>
                <Passages
                  patients={patients.filter((p) => !p.isFinalized)}
                  setPatients={setPatients}
                />
              </PageSlide>
            </PrivateRoute>
          }
        />

        <Route
          path="/todos"
          element={
            <PrivateRoute>
              <Todos />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin/actes"
          element={
            <RequireAdmin>
              <AdminActes />
            </RequireAdmin>
          }
        />

        <Route
          path="/admin/traumato"
          element={
            <RequireAdmin>
              <AdminTraumato />
            </RequireAdmin>
          }
        />

        <Route
          path="/admin/print-codes"
          element={
            <RequireAdmin>
              <AdminRoute>
                <PageSlide>
                  <AdminPrintCodes />
                </PageSlide>
              </AdminRoute>
            </RequireAdmin>
          }
        />

        <Route
          path="/admin/formation-continue"
          element={
            <RequireAdmin>
              <AdminRoute>
                <PageSlide>
                  <AdminFormationContinue />
                </PageSlide>
              </AdminRoute>
            </RequireAdmin>
          }
        />

        <Route
          path="/admin/salles"
          element={
            <RequireAdmin>
              <AdminRoute>
                <PageSlide>
                  <AdminSallesGroupes />
                </PageSlide>
              </AdminRoute>
            </RequireAdmin>
          }
        />

        <Route
          path="/admin/equipements"
          element={
            <RequireAdmin>
              <AdminRoute>
                <PageSlide>
                  <AdminEquipements />
                </PageSlide>
              </AdminRoute>
            </RequireAdmin>
          }
        />

        <Route
          path="/admin/parametres"
          element={
            <RequireAdmin>
              <AdminRoute>
                <PageSlide>
                  <AdminParametres />
                </PageSlide>
              </AdminRoute>
            </RequireAdmin>
          }
        />

        <Route
          path="/admin/stats"
          element={
            <RequireAdmin>
              <AdminRoute>
                <PageSlide>
                  <AdminStats />
                </PageSlide>
              </AdminRoute>
            </RequireAdmin>
          }
        />

        <Route
          path="/admin/todos"
          element={
            <RequireAdmin>
              <AdminRoute>
                <PageSlide>
                  <AdminTodoConfig />
                </PageSlide>
              </AdminRoute>
            </RequireAdmin>
          }
        />

        <Route
          path="/admin/users2"
          element={
            <RequireAdmin>
              <AdminRoute>
                <PageSlide>
                  <AdminUsers />
                </PageSlide>
              </AdminRoute>
            </RequireAdmin>
          }
        />

        <Route
          path="/admin/users"
          element={
            <RequireAdmin>
              <AdminRoute>
                <PageSlide>
                  <AdminUsers2 />
                </PageSlide>
              </AdminRoute>
            </RequireAdmin>
          }
        />

        <Route
          path="/admin/audit"
          element={
            <RequireAdmin>
              <AdminAudit />
            </RequireAdmin>
          }
        />

        <Route
          path="/fiche/:id"
          element={
            <PrivateRoute>
              <PageSlide>
                <FichePatient patients={patients} setPatients={setPatients} />
              </PageSlide>
            </PrivateRoute>
          }
        />

        <Route
          path="/arret-travail/:id"
          element={
            <PrivateRoute>
              <PageSlide>
                <ArretTravail />
              </PageSlide>
            </PrivateRoute>
          }
        />

        <Route 
          path="/arret-travail-pdf/:id"
          element={
            <PrivateRoute>
              <PageSlide>
                <FormulaireArretTravail mode="pdf"/>
              </PageSlide>
            </PrivateRoute>
          }
        />

        <Route 
          path="/arret-travail-todo/:id"
          element={
            <PrivateRoute>
              <PageSlide>
                <FormulaireArretTravail mode="todo"/>
              </PageSlide>
            </PrivateRoute>
          }
        />

        <Route
          path="/cotation/:id"
          element={
            <PrivateRoute>
              <PageSlide>
                <CotationWizard
                  key={location.pathname} // force le remount à chaque patient
                  patients={patients}
                  setPatients={setPatients}
                />
              </PageSlide>
            </PrivateRoute>
          }
        />

        <Route
          path="/sortie"
          element={
            <Sortie
              patients={patients}
              setPatients={setPatients}
            />
          }
        />
        
        <Route
          path="/sortie/:id"
          element={
            <SortiePatient
              patients={patients}
              setPatients={setPatients}
            />
          }
        />
        
        <Route
          path="/scan"
          element={
            <PrivateRoute>
              <ScanCodeScreen />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin-sortie"
          element={
            <PrivateRoute>
              <AdminRoute>
                <PageSlide>
                  <AdminSortie />
                </PageSlide>
              </AdminRoute>
            </PrivateRoute>
          }
        />

        <Route
          path="/debug"
          element={
            <PageSlide>
              <PageDebug patients={patients} />
            </PageSlide>
          }
        />

        {/* catch-all */}
        <Route path="*" element={<Navigate to="/arrivee" replace />} />
      </Routes>
    </AnimatePresence>
  );
}
